import { CellText } from '@increasecard/typed-components';
import { InputHTMLAttributes } from 'react';
import styled from 'styled-components';

export interface FloatingLabelInputProps
  extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  errorMessage?: string;
  description?: string;
  /**
   * Background color for the label so it covers the border beneath it.
   * Default is white. Change it if the input is placed on a different background color.
   */
  labelBgColor?: string;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  ${CellText} {
    margin-left: 14px;
  }
`;

const InputContainer = styled.div<{
  $hasError?: boolean;
  $labelBgColor: string;
}>`
  position: relative;
  width: 100%;

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 12px 14px;
    font-size: 16px;
    border-radius: 4px;
    outline: none;
    background-color: white;
    color: #333;
    border: 1px solid ${({ $hasError }) => ($hasError ? '#f44336' : '#9e9e9e')};

    &:hover {
      border-color: ${({ $hasError }) => ($hasError ? '#f44336' : '#212121')};
    }

    &:focus {
      border-color: ${({ $hasError }) => ($hasError ? '#f44336' : '#3f51b5')};
      border-width: 2px;
      /* Adjust padding to prevent jumping when border width changes */
      padding: 11px 13px;
    }

    /* Target the label when input is focused OR has value (not showing placeholder) */
    &:focus + label,
    &:not(:placeholder-shown) + label {
      top: -6px;
      left: 10px;
      font-size: 12px;
      background-color: ${({ $labelBgColor }) => $labelBgColor};
      padding: 0 4px;
      color: ${({ $hasError }) => ($hasError ? '#f44336' : '#3f51b5')};
    }

    /* Target the label when input is NOT focused and NOT empty */
    &:not(:focus):not(:placeholder-shown) + label {
      color: ${({ $hasError }) => ($hasError ? '#f44336' : '#757575')};
    }
  }

  label {
    position: absolute;
    left: 14px;
    top: 12px;
    color: ${({ $hasError }) => ($hasError ? '#f44336' : '#757575')};
    font-size: 16px;
    font-weight: 400;
    pointer-events: none;
    transition: all 0.1s ease-in;
    margin: 0;
    display: inline-block;
  }
`;

const ErrorText = styled.span`
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 14px;
`;

export function FloatingLabelInput({
  label,
  errorMessage,
  required,
  id,
  labelBgColor = '#f5f5f5',
  description,
  ...props
}: FloatingLabelInputProps): JSX.Element {
  const hasError = !!errorMessage;
  const inputId = id || `floating-input-${label.replace(/\s+/g, '-')}`;

  return (
    <Wrapper>
      <InputContainer $hasError={hasError} $labelBgColor={labelBgColor}>
        <input
          id={inputId}
          /* The placeholder MUST be a single space to trigger :placeholder-shown correctly while keeping it invisible inside */
          placeholder=" "
          {...props}
        />
        <label htmlFor={inputId}>
          {label}
          {required && ' *'}
        </label>
      </InputContainer>
      {description && <CellText>{description}</CellText>}
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Wrapper>
  );
}
